import React, { Component } from 'react'
import cx from 'classnames'
import { debounce } from 'lodash'
import PropTypes from 'prop-types'

import TextField from '../textfield/textfield'

import styles from './searchfield.module.css'

const propTypes = {
    className: PropTypes.string,
    containerClassName: PropTypes.string,
    containerStyle: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    disabled: PropTypes.bool,
    doFilteringAfterChars: PropTypes.number,
    error: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.object // eslint-disable-line react/forbid-prop-types
        // PropTypes.instanceOf(Error) won't work due to babel6
    ]),
    full: PropTypes.bool,
    initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leftIcon: PropTypes.node,
    leftIconClassName: PropTypes.string, // @deprecated: Set className directly in leftIcon node instead
    // @deprecated: Set props directly in leftIcon n ode instead
    leftIconProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    // @deprecated: Set style directly in leftIcon node instead
    leftIconStyle: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    maxLength: PropTypes.number,
    noHighlightFrame: PropTypes.bool,
    onChange: PropTypes.func,
    onChangeDelay: PropTypes.number,
    onKeyDown: PropTypes.func,
    placeholder: PropTypes.string,
    resetButton: PropTypes.bool,
    style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

const defaultProps = {
    disabled: false,
    doFilteringAfterChars: 1,
    noHighlightFrame: false,
    onChange: Function.prototype,
    onChangeDelay: 250,
    onKeyDown: Function.prototype,
    resetButton: true,
    type: 'search'
}

/**
 * Specialized {@link TextField} to be used as searchfield / searchbox to filter
 * data or to search for text. A lot of props are just passed through to the TextField,
 * except the ones that are necessary for filtering...
 *
 * @see TextField
 *
 * @example <caption>Default usage</caption>
 * <SearchField placeholder='search...' onChange={(value) => {filterFn(value)}} onChangeDelay={1000} />
 * @example <caption>Control input value from parent component, value will update everytime the parent renders</caption>
 * <SearchField value={value} onChange={(value) => {filterFn(value)}} />
 * @example <caption>Let the input value be controlled by TextFields internal state</caption>
 * <SearchField initialValue={value} onChange={(value) => {filterFn(value)}} />
 *
 * @param {string} [props.className] - Additional css className for the input element, passed through to TextField
 * @param {Object} [props.style] - Additional css style for the input element, passed through to TextField
 * @param {string|number} [props.value] - Input value to be stored in internal state, passed through to TextField
 * @param {string|number} [props.initialValue] - Input value to be stored in internal state, passed through to TextField
 * @param {string} [props.type='search'] - Input type (text, number, email, ....), passed through to TextField
 * @param {number} [props.maxLength] - The maximum length of characters, passed through to TextField
 * @param {string} [props.placeholder] - The placeholder text for empty inputs, passed through to TextField
 * @param {Function} [props.onChange=noop] - Input value change callback for custom filterring
 * @param {number} [props.onChangeDelay=0] - Wait provided milliseconds and then invoke onChange handler
 * @param {number} [props.doFilteringAfterChars=1] - Do a filtering first after the user has entered xxx characters
 * @param {number} [props.disabled=false] - If the search field is disabled.
 * @param {Function|Object|boolean} [props.error] - ErrorPopover state, passed through to TextField
 * @param {string} [props.containerClassName] - Additional className for the container, passed through to TextField
 * @param {Object} [props.containerStyle] - Additional css style for the container element, passed through to TextField
 * @param {Object} [props.leftIcon] - Left icon component, passed through to TextField
 * @param {string} [props.leftIconClassName] - DEPRECATED: Additional css style for the left icon, passed to TextField
 * @param {Object} [props.leftIconProps] - DEPRECATED: Additional props for the left icon, passed to TextField
 * @param {Object} [props.leftIconStyle] - DEPRECATED: Additional css style for the left icon, passed to TextField
 * @param {boolean} [props.resetButton=true] - Whether to show the reset button or not
 * @param {boolean} [props.noHighlightFrame=false] - Whether to disable css frame highlighting in textfield
 * @param {boolean} [props.full=false] - Whether to disable css max-width or not, passed through to TextField
 * @returns {ReactElement}
 */
class SearchField extends Component {
    /**
     * @contructs
     * @param {Object} [props]
     * @returns {void}
     */
    constructor(props) {
        super(props)
        this.onChange = this.onChange.bind(this)
        this.onPropsChangeDelayed = debounce(props.onChange, props.onChangeDelay)
        this.onKeyDown = this.onKeyDown.bind(this)
    }

    componentDidUpdate(prevProps) {
        if (prevProps.onChange !== this.props.onChange || prevProps.onChangeDelay !== this.props.onChangeDelay) {
            this.onPropsChangeDelayed = debounce(this.props.onChange, this.props.onChangeDelay)
        }
    }

    /**
     * @function
     * @returns {ReactElement}
     */
    render() {
        const {
            className,
            containerClassName,
            containerStyle,
            disabled,
            error,
            full,
            initialValue,
            leftIcon,
            leftIconClassName,
            leftIconProps,
            leftIconStyle,
            maxLength,
            noHighlightFrame,
            placeholder,
            resetButton,
            style,
            type,
            value
        } = this.props
        const composedClassName = cx(styles['searchfield'], className, {
            [styles['no-highlight-frame']]: noHighlightFrame
        })

        return (
            <TextField
                className={composedClassName}
                onChange={this.onChange}
                onKeyDown={this.onKeyDown}
                ref={ref => (this.containerRef = ref)}
                {...{
                    containerClassName,
                    containerStyle,
                    disabled,
                    error,
                    full,
                    initialValue,
                    leftIcon,
                    leftIconClassName,
                    leftIconProps,
                    leftIconStyle,
                    maxLength,
                    placeholder,
                    resetButton,
                    style,
                    type,
                    value
                }}
            />
        )
    }

    /**
     * Handle change event.
     *
     * @function
     * @private
     * @param {SyntheticEvent} Reacts internal event object
     * @returns {void}
     */
    onChange(event) {
        const { doFilteringAfterChars } = this.props
        let { value } = event.target

        if (value === undefined) {
            value = ''
        }

        if (value) {
            if (value.length >= doFilteringAfterChars) {
                this.onPropsChangeDelayed(value)
            } else {
                /**
                 * May be useful when the user deletes his filter entry
                 * (probably the using component needs to be updating it's state
                 * or something similar when the filter is reset to '')
                 */
                this.onPropsChangeDelayed('')
            }
        } else {
            this.onPropsChangeDelayed(value)
        }
    }

    /** @private */
    onKeyDown(event) {
        this.props.onKeyDown(event)
    }

    /**
     * Get dom node from container element. Just here for backwards
     * compatibility - Refs shouldn't be used anymore!
     *
     * @function
     * @deprecated
     * @returns {Object} The dom node representation from the wrapper
     */
    getDOMNode() {
        return this.containerRef
    }
}

SearchField.propTypes = propTypes
SearchField.defaultProps = defaultProps

export default SearchField
